import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { GenericInlineItemListTranslationService } from './generic-inline-item-list-translation.service';
import { GenericInlineItemListTranslations, GenericInlineItemListTranslationInput } from './generic-inline-item-list-translations.interface';
import { focusNearestElement } from 'app/shared/utils/focusHelper';

export interface GenericInlineItemListItemContext<T> {
  $implicit: T;
  index: number;
  total: number;
  readonly: boolean;
}

@Component({
  selector: 'app-generic-inline-item-list',
  templateUrl: './generic-inline-item-list.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class GenericInlineItemListComponent<T = any> implements OnInit, OnChanges {
  @Input() idPrefix: string = 'generic-inline-item-list';
  @Input() data: T[] = [];
  @Input() readonly: boolean = false;
  @Input() translations?: GenericInlineItemListTranslationInput;
  @Input() translationParams: Record<string, string | number> = {};
  @Input() canRemove: (item: T, index: number) => boolean = () => true;

  @Output() append = new EventEmitter<void>();
  @Output() remove = new EventEmitter<{ item: T; index: number }>();

  @ContentChild(TemplateRef) itemTemplate: TemplateRef<GenericInlineItemListItemContext<T>>;

  public resolvedTranslations: GenericInlineItemListTranslations;

  constructor(private translationService: GenericInlineItemListTranslationService) {
    this.resolvedTranslations = this.translationService.getResolvedTranslations();
  }

  ngOnInit(): void {
    this.resolveTranslations();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['translations'] || changes['translationParams']) && !changes['translations']?.firstChange) {
      this.resolveTranslations();
    }
  }

  private resolveTranslations(): void {
    this.resolvedTranslations = this.applyTranslationParams(
      this.translationService.getResolvedTranslations(this.translations)
    );
  }

  private applyTranslationParams(translations: GenericInlineItemListTranslations): GenericInlineItemListTranslations {
    if (!this.translationParams || Object.keys(this.translationParams).length === 0) {
      return translations;
    }
    return this.interpolateObject(translations) as GenericInlineItemListTranslations;
  }

  private interpolateObject(obj: any): any {
    if (typeof obj === 'string') {
      return obj.replace(/\{(\w+)\}/g, (_, key) => {
        const val = this.translationParams[key];
        return val !== undefined ? String(val) : `{${key}}`;
      });
    }
    if (typeof obj === 'object' && obj !== null) {
      const result: any = {};
      for (const key of Object.keys(obj)) {
        result[key] = this.interpolateObject(obj[key]);
      }
      return result;
    }
    return obj;
  }

  public getItemContext(item: T, index: number): GenericInlineItemListItemContext<T> {
    return {
      $implicit: item,
      index,
      total: this.data.length,
      readonly: this.readonly,
    };
  }

  public appendItem(): void {
    this.append.emit();
    focusNearestElement(`#${this.idPrefix}-list > li:last-child`, {
      delay: 50,
      preventScroll: false,
    });
  }

  public removeItem(index: number): void {
    const item = this.data[index];
    const lengthBefore = this.data.length;
    this.remove.emit({ item, index });

    if (this.data.length < lengthBefore) {
      if (lengthBefore > 1) {
        const target = Math.min(index, lengthBefore - 2);
        focusNearestElement(`#${this.idPrefix}-list > li:nth-child(${target + 1})`, {
          delay: 50,
          preventScroll: false,
        });
      } else {
        focusNearestElement(`[aria-controls="${this.idPrefix}-list"]`, {
          delay: 50,
          preventScroll: false,
        });
      }
    }
  }

  public t(key: string, params?: { [key: string]: string | number }): string {
    return this.translationService.getFromResolved(key, this.resolvedTranslations, params);
  }
}
